import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import SponsorStrip from "@/components/editorial/SponsorStrip";
import api from "@/lib/api";
import { EDITORIAL_IMAGES, STATS } from "@/data/content";

const TEAM = [
  {
    name: "Paco Herrera",
    role: "CEO · Chief Creative Officer",
    bio: "Director creativo y fundador. 20 años produciendo narrativas para marcas y medios.",
    photo: "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/9a7b6beb2e7ab31e59e82246953a3dcd9c187252267d0a063a0d9068d89408a1.jpeg",
  },
  {
    name: "Mariola Zepeda",
    role: "COO · Growth & Community",
    bio: "Construcción de comunidad y alianzas con marcas. Operación editorial.",
    photo: "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/73f1d03672b0f2c40e7ce5f466d9bdcffccc79861a9b525da9ab2adc763868fe.jpeg",
  },
  {
    name: "Luciana Ríos",
    role: "Social & Content Lead",
    bio: "Editora de contenido vertical y estrategia social multicanal.",
    photo: "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/fefe390234bbb7b9208b256692be3744f96f9e0ceb6949be2752b1383c779908.jpeg",
  },
  {
    name: "Diego Navarro",
    role: "Distribución & Alianzas de Medios",
    bio: "Distribución multicanal y partnerships con medios aliados.",
    photo: "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/fb46310932c71ba69b5026616fc230ecb0e70b4c337ad7edcebdb0d33a95ea7f.jpeg",
  },
  {
    name: "Camila Vega",
    role: "Sponsor Executive",
    bio: "Relación con marcas fundadoras y categoría. Propuestas a la medida.",
    photo: "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/7debc227ba93fef47ecbbd3d26e1dd59772189ab1d436d70092a3702477672d3.jpeg",
  },
];

const SITE_URL = "https://mujeres-motor.com";

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || form.message.trim().length < 10) {
      toast.error("Completa los campos. El mensaje debe tener al menos 10 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      const clean = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "string" && v.trim() !== "") clean[k] = v.trim();
      }
      const { data } = await api.post("/submissions/contact", clean);
      toast.success(data.message);
      setDone(true);
      window.scrollTo({ top: document.getElementById("contacto")?.offsetTop || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const personsLd = TEAM.map((t) => ({
    "@type": "Person",
    name: t.name,
    jobTitle: t.role,
    image: t.photo,
    worksFor: { "@type": "Organization", name: "Mujeres Motor" },
  }));
  const orgLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "Mujeres Motor",
        url: SITE_URL,
        logo: `${SITE_URL}/og-default.jpg`,
        description: "Plataforma editorial documental para las mujeres que están moviendo México.",
        sameAs: ["https://www.instagram.com/mujeres_motor"],
        employee: personsLd,
      },
    ],
  };

  return (
    <SiteLayout transparent>
      <></>

      {/* Hero */}
      <section data-testid="about-hero" className="relative min-h-[70vh] flex items-end bg-charcoal text-cream overflow-hidden pb-20">
        <img src={EDITORIAL_IMAGES.community} alt="Comunidad Mujeres Motor" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/50 to-charcoal" />
        <Container className="relative z-10">
          <Eyebrow tone="gold" className="mb-5 block">Nosotras</Eyebrow>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">
            Una plataforma <span className="italic">documental</span> para las mujeres que están moviendo México.
          </h1>
          <p className="mt-6 text-cream/75 text-lg max-w-2xl">
            Construimos narrativas cinematográficas, conectamos comunidades y
            conectamos marcas con propósito. Esta es nuestra misión.
          </p>
        </Container>
      </section>

      {/* Manifesto */}
      <section data-testid="about-manifesto" className="bg-cream py-24 md:py-32">
        <Container>
          <div className="max-w-3xl mx-auto">
            <Eyebrow tone="terracotta" className="mb-6 block">Manifiesto</Eyebrow>
            <div className="font-serif font-light text-[clamp(1.5rem,2.6vw,2.25rem)] leading-[1.25] text-charcoal space-y-8">
              <p>
                Creemos que <span className="italic">las historias de las mujeres</span> que están moviendo México no pueden seguir esperando el permiso de nadie para contarse.
              </p>
              <p>
                Creemos que el liderazgo femenino mexicano es un motor económico, cultural y social que merece una plataforma a la altura de su ambición.
              </p>
              <p>
                Creemos que <span className="italic">las marcas que se asocian</span> con estas historias no las patrocinan: se suman al cambio que ya está ocurriendo.
              </p>
            </div>
            <p className="mt-12 text-terracotta font-serif italic text-lg">— Equipo Mujeres Motor</p>
          </div>
        </Container>
      </section>

      {/* Mission + Stats */}
      <section className="bg-cream-dark py-24 md:py-28 border-t border-charcoal/5" data-testid="about-mission">
        <Container>
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            <div>
              <Eyebrow tone="terracotta" className="mb-5 block">Qué hacemos</Eyebrow>
              <h2 className="font-serif font-light text-4xl md:text-5xl mb-8 leading-[1.1]">Documentar, <span className="italic">conectar</span>, amplificar.</h2>
              <div className="space-y-5 text-charcoal/80 text-[17px] leading-relaxed">
                <p>Documentamos las historias de mujeres que están liderando industrias, sectores y comunidades desde México. No buscamos íconos ni inspiración genérica: buscamos operación concreta, tensión real y la segunda capa humana.</p>
                <p>Conectamos a estas mujeres entre sí a través de una red privada, mentorías y eventos curados. La comunidad no es un beneficio extra del contenido: es el contenido.</p>
                <p>Amplificamos sus voces en una plataforma editorial premium —documental, podcast, social, blog, eventos— diseñada para estar a la altura de lo que nos cuentan.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {STATS.map((s) => (
                <div key={s.label} className="border-l-2 border-terracotta pl-5">
                  <p className="font-serif text-5xl md:text-6xl font-light text-charcoal leading-none">{s.value}</p>
                  <p className="text-xs uppercase tracking-[0.22em] text-charcoal/60 mt-3">{s.label}</p>
                </div>
              ))}
              <div className="col-span-2 mt-4 aspect-[16/10] overflow-hidden rounded-sm">
                <img src={EDITORIAL_IMAGES.production} alt="Producción editorial" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Team */}
      <section className="bg-charcoal text-cream py-24 md:py-32" data-testid="about-team">
        <Container>
          <div className="max-w-2xl mb-14">
            <Eyebrow tone="gold" className="mb-5 block">Equipo</Eyebrow>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Las personas <span className="italic">detrás de la plataforma.</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {TEAM.map((m) => (
              <figure key={m.name} data-testid={`team-${m.name.split(" ")[0].toLowerCase()}`} className="group">
                <div className="relative aspect-[3/4] overflow-hidden rounded-sm bg-charcoal-soft mb-5">
                  <img src={m.photo} alt={m.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-cream/5" />
                </div>
                <figcaption>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-gold mb-1.5">{m.role}</p>
                  <h3 className="font-serif text-2xl md:text-3xl font-light mb-2">{m.name}</h3>
                  <p className="text-sm text-cream/70 leading-relaxed">{m.bio}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </Container>
      </section>

      {/* Aliados editoriales */}
      <section className="bg-cream py-24 md:py-28" data-testid="about-allies">
        <Container>
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <Eyebrow tone="terracotta" className="mb-5 block">Aliados editoriales 2026</Eyebrow>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05]">Marcas y socios que hacen <span className="italic">posible Mujeres Motor.</span></h2>
          </div>
          <div className="space-y-12 max-w-5xl mx-auto">
            <SponsorStrip tier="founding" eyebrow="Founding Partners" tone="light" size="lg" centered testid="about-founding" />
            <SponsorStrip tier="category" eyebrow="Category Partners" tone="light" size="md" centered testid="about-category" />
            <SponsorStrip tier="episode" eyebrow="Episode Sponsors · Temporada 1" tone="light" size="md" centered testid="about-episode" />
            <SponsorStrip tier="content" eyebrow="Content &amp; Media Partners" tone="light" size="sm" centered testid="about-content" />
          </div>
          <div className="text-center mt-14">
            <Link to="/for-brands" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-terracotta hover:text-[#A64826] transition-colors">
              Conviértete en aliado <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Container>
      </section>

      {/* Baklight production credit */}
      <section className="bg-cream-dark py-16 border-t border-charcoal/5" data-testid="about-baklight">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <Eyebrow tone="terracotta" className="mb-4 block">Producido por</Eyebrow>
            <h3 className="font-serif italic text-3xl md:text-4xl text-charcoal mb-4">Baklight Productions</h3>
            <p className="text-charcoal/70 max-w-lg mx-auto">
              Casa productora documental especializada en narrativas con propósito. Con más de 20 años creando historias en cine, televisión, publicidad y contenido digital.
            </p>
            <a href="https://baklight.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-terracotta hover:text-[#A64826] mt-5">
              Visita Baklight <ArrowRight className="h-3 w-3" />
            </a>
          </div>
        </Container>
      </section>

      {/* Contact */}
      <section id="contacto" data-testid="about-contact" className="bg-charcoal text-cream py-24 md:py-32">
        <Container>
          <div className="max-w-2xl mb-12">
            <Eyebrow tone="gold" className="mb-5 block">Contacto</Eyebrow>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05]">Escríbenos.</h2>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-2 space-y-6 text-sm">
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-1 text-gold" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-cream/50 mb-0.5">Email</p>
                  <a href="mailto:hola@mujeres-motor.com" className="text-cream hover:text-gold transition-colors">hola@mujeres-motor.com</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-1 text-gold" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-cream/50 mb-0.5">Teléfono</p>
                  <p className="text-cream/80">+52 (55) — por solicitud</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-gold" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-cream/50 mb-0.5">Ubicación</p>
                  <p className="text-cream/80">Ciudad de México</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              {done ? (
                <div data-testid="contact-success" className="bg-charcoal-soft border border-gold/30 p-10 rounded-sm">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-gold mb-3">Recibido</p>
                  <h3 className="font-serif text-2xl md:text-3xl font-light mb-3">Gracias por escribirnos.</h3>
                  <p className="text-cream/75">Te responderemos en un máximo de 3 días hábiles desde hola@mujeres-motor.com.</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
                  <input type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => upd("website", e.target.value)} style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }} aria-hidden="true" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="contact-name" placeholder="Nombre completo *" value={form.name} onChange={(e) => upd("name", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" required />
                    <input data-testid="contact-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => upd("email", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" required />
                  </div>
                  <input data-testid="contact-subject" placeholder="Asunto *" value={form.subject} onChange={(e) => upd("subject", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" required />
                  <textarea data-testid="contact-message" placeholder="Tu mensaje *" value={form.message} onChange={(e) => upd("message", e.target.value)} rows="6" className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none resize-none" required minLength={10} />
                  <button type="submit" data-testid="contact-submit" disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms] disabled:opacity-60">
                    {submitting ? "Enviando…" : "Enviar mensaje"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
